import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import {
  Users,
  BookOpen,
  Award,
  Heart,
  Target,
  Clock,
  Shield,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import IllustrazioneFPC from '../ui/IllustrazioneFPC.png';

function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function registrationRedirect() {
    navigate('/register');
  }

  const features = [
    {
      icon: <Users className="w-6 h-6 text-white" />,
      title: 'Affidati a Professionisti',
      description:
        'Selezioniamo i migliori insegnanti privati per garantire qualità e competenza. Solo veri professionisti.',
      color: 'bg-fpcred',
    },
    {
      icon: <Clock className="w-6 h-6 text-white" />,
      title: 'Programma le lezioni',
      description: 'Piattaforma facile e veloce. Prenota le tue lezioni in pochi click e in totale sicurezza.',
      color: 'bg-coral',
    },
    {
      icon: <Target className="w-6 h-6 text-white" />,
      title: 'Percorsi personalizzati',
      description: 'Insegniamo un metodo di studio e forniamo le conoscenze per affrontare qualsiasi materia.',
      color: 'bg-fpcred',
    },
    {
      icon: <Shield className="w-6 h-6 text-white" />,
      title: 'Team esperto',
      description: 'Esperienza nella risoluzione di problemi, incluso il supporto per ragazzi con BES o DSA.',
      color: 'bg-coral',
    },
  ];

  const stats = [
    { value: '40+', label: 'Insegnanti' },
    { value: '10K+', label: 'Ore di Lezione' },
    { value: '2014', label: 'Anno di Fondazione' },
  ];

  return (
    <>
      <SEO
        title="Chi Siamo - La Nostra Storia"
        description="Formando PerCorsi: offriamo ripetizioni di qualità con insegnanti dedicati. Metodo efficace e percorsi personalizzati per ogni studente."
        canonical="https://formandopercorsi.com/chi-siamo"
      />
      <main className="min-h-screen bg-fpcwhite font-sans">
        {/* Decorative Background Pattern */}
        <div
          className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(#cc0000 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}></div>

        {/* Hero Section */}
        <section className="relative z-10 container mx-auto px-4 pb-16 lg:pt-28 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image - First on mobile, second on desktop */}
            <div className="relative order-1 lg:order-2">
              <img
                src={IllustrazioneFPC}
                alt="Illustrazione Formando Percorsi"
                className="w-full rounded-3xl shadow-lg"
              />
            </div>

            {/* Text - Second on mobile, first on desktop */}
            <div className="space-y-6 lg:space-y-8 order-2 lg:order-1">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Costruiamo insieme il tuo{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-fpcred to-coral">
                  futuro scolastico
                </span>
              </h1>

              <p className="text-xl sm:text-2xl lg:text-xl text-slate-600 leading-relaxed max-w-xl">
                Ci dedichiamo con passione alle lezioni individuali, offrendo supporto in ogni materia. Dai successi
                locali all'esperienza nazionale, il nostro obiettivo è l'autonomia dello studente.
              </p>

              <div className="flex gap-4">
                <button
                  className="bg-fpcred text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:bg-buttonhover transition-all hover:-translate-y-1 flex items-center gap-2 text-base sm:text-lg"
                  onClick={registrationRedirect}>
                  Inizia ora
                </button>
                <div className="flex items-center gap-4 px-6 py-4 bg-white rounded-xl shadow-sm border border-slate-100">
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-500">
                        {/* Placeholder avatars */}U{i}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm sm:text-base font-medium text-slate-600">Unisciti a 500+ studenti</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section (Full Width) */}
        <section
          className="relative bg-slate-900 text-white py-16 sm:py-20 lg:py-24 overflow-hidden rounded-lg mx-2"
          aria-label="La nostra filosofia">
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-fpcred rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-coral rounded-full blur-3xl opacity-20"></div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <Heart className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-6 sm:mb-8 text-fpcred" />
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 sm:mb-8 tracking-tight">
                Qualità, non quantità.
              </h2>
              <p className="text-xl sm:text-2xl lg:text-2xl text-slate-300 leading-relaxed mb-8">
                Non siamo un marketplace anonimo con migliaia di insegnanti. Siamo un team selezionato. Con{' '}
                <strong className="text-white">Formando Percorsi</strong> lo studente è al centro: progettiamo percorsi
                su misura e offriamo un accompagnamento costante.
              </p>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="container mx-auto px-4 py-16 sm:py-20 lg:py-24 -mt-10" aria-label="I nostri punti di forza">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-7 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 flex flex-col items-start lg:items-center">
                <div className="flex items-center gap-4 mb-4 lg:flex-col lg:gap-0">
                  <div
                    className={`${feature.color} w-14 h-14 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md lg:mb-6`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 lg:mb-3">{feature.title}</h3>
                </div>
                <p className="text-slate-600 text-md leading-relaxed lg:text-center">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Method & Continuity (Two Columns) */}
        <section className="container mx-auto px-4 py-12 sm:py-16 mb-8 sm:mb-16" aria-label="Metodo e continuità">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            {/* Card 1 */}
            <div className="bg-fpcpink p-6 sm:p-8 border border-fpcred rounded-md">
              <div className="flex items-start gap-4">
                <div className="bg-white p-3 rounded-full shadow-sm flex-shrink-0">
                  <Lightbulb className="w-8 h-8 sm:w-9 sm:h-9 text-fpcred" />
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-slate-900 mb-3 sm:mb-4">
                    Metodo Efficace
                  </h3>
                  <p className="text-slate-700 text-base sm:text-lg lg:text-base leading-relaxed">
                    Per essere un nostro insegnante non basta conoscere la materia. È fondamentale saperla trasmettere
                    attraverso un{' '}
                    <span className="font-semibold text-fpcred bg-fpcpink px-1 rounded">metodo di studio</span> provato
                    ed efficace, adattabile ad ogni studente.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-fpcpink p-6 sm:p-8 border border-fpcred rounded-md">
              <div className="flex items-start gap-4">
                <div className="bg-white p-3 rounded-full shadow-sm flex-shrink-0">
                  <CheckCircle2 className="w-8 h-8 sm:w-9 sm:h-9 text-coral" />
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-slate-900 mb-3 sm:mb-4">
                    Continuità Garantita
                  </h3>
                  <p className="text-slate-700 text-base sm:text-lg lg:text-base leading-relaxed">
                    Ogni studente viene affiancato da un insegnante dedicato che garantisce{' '}
                    <span className="font-semibold text-coral bg-[#fef2f2] px-1 rounded">continuità didattica</span>.
                    Non cambierai insegnante ogni settimana: costruiamo un rapporto di fiducia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Footer Section */}
        <section className="container mx-auto px-4 pb-16 sm:pb-24">
          <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-fpcred opacity-20 rounded-full translate-x-1/2 translate-y-1/2 blur-2xl"></div>

            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                Pronto a vedere la differenza?
              </h2>
              <p className="text-xl sm:text-2xl lg:text-xl text-slate-300 mb-8 sm:mb-10 leading-relaxed">
                Scegliere <strong className="text-white">Formando Percorsi</strong> è il primo passo verso un
                apprendimento più sereno, efficace e gratificante.
              </p>
              <button
                className="bg-fpcred text-white px-10 py-4 rounded-full text-lg sm:text-xl font-bold hover:bg-buttonhover hover:shadow-lg hover:shadow-fpcred/50 transform hover:scale-105 transition-all duration-300"
                onClick={registrationRedirect}>
                Prenota la tua prima lezione
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default About;
